import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations } from "@/i18n/translations";
import { api, ensureAnonToken, getLang, setLang as persistLang } from "@/lib/api";
import { resetAnonPromptShown } from "@/lib/anonPrompt";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [lang, setLangState] = useState(getLang());
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [anonReady, setAnonReady] = useState(false);
  const [anonState, setAnonStateLocal] = useState({});
  const [phaseState, setPhaseState] = useState(null);

  const refreshPhaseState = useCallback(async () => {
    try {
      const res = await api.get("/phase-progress");
      setPhaseState(res.data);
      return res.data;
    } catch {
      setPhaseState(null);
      return null;
    }
  }, []);

  const setLang = (l) => {
    persistLang(l);
    setLangState(l);
  };

  const t = translations[lang] || translations.it;

  const refreshUser = useCallback(async () => {
    try {
      // Skip /auth/me during OAuth callback fragment handling
      if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
        setLoadingUser(false);
        return;
      }
      const res = await api.get("/auth/me");
      setUser(res.data.user);
    } catch {
      setUser(null);
    } finally {
      setLoadingUser(false);
    }
  }, []);

  // Load phase state whenever the logged-in user changes
  useEffect(() => {
    if (user) refreshPhaseState();
    else setPhaseState(null);
  }, [user, refreshPhaseState]);

  useEffect(() => {
    (async () => {
      await ensureAnonToken();
      setAnonReady(true);
      try {
        const res = await api.get("/anon/state");
        setAnonStateLocal(res.data.state || {});
      } catch {}
      await refreshUser();
    })();
  }, [refreshUser]);

  const updateAnonState = async (patch) => {
    const res = await api.post("/anon/state", patch);
    setAnonStateLocal(res.data.state);
    return res.data.state;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    // Clear the anon prompt "shown" flag so a fresh anon journey after
    // logout will get the informational nudge again on first mutation.
    resetAnonPromptShown();
  };

  return (
    <AppContext.Provider value={{
      lang, setLang, t,
      user, loadingUser, refreshUser, logout,
      anonReady, anonState, updateAnonState,
      phaseState, refreshPhaseState,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
